/**
 * Shared chat state hook for preserving state across mode switches (popup <-> fullscreen)
 * 
 * This hook manages the core chat state that needs to persist when switching between
 * popup and fullscreen modes. Without this, switching modes would cause the component
 * to unmount and lose all message history, status messages, and polling state.
 */

import { useState, useCallback, useMemo } from 'react';
import type { MessageInterface, PendingMessage } from './useMessageProcessing';
import type { ParlantEvent } from '../types/parlant';

/**
 * Default pending message state
 */
const DEFAULT_PENDING_MESSAGE: PendingMessage = {
  kind: 'message',
  source: 'customer',
  creationUtc: new Date(),
  serverStatus: 'pending',
  offset: 0,
  correlationId: '',
  data: { message: '' },
  status: null,
};

/**
 * Shared state that persists across mode switches
 */
export interface SharedChatState {
  messages: MessageInterface[];
  statusMessage: MessageInterface | null;
  pendingMessage: PendingMessage;
  events: ParlantEvent[];
  lastOffset: number;
}

/**
 * Setters for shared state
 */
export interface SharedChatStateSetters {
  setMessages: React.Dispatch<React.SetStateAction<MessageInterface[]>>;
  setStatusMessage: React.Dispatch<React.SetStateAction<MessageInterface | null>>;
  setPendingMessage: React.Dispatch<React.SetStateAction<PendingMessage>>;
  setEvents: React.Dispatch<React.SetStateAction<ParlantEvent[]>>;
  setLastOffset: React.Dispatch<React.SetStateAction<number>>;
  /** Reset all shared state to initial values (optional, added by ChatBot) */
  resetSharedState?: () => void;
}

/**
 * Return type for useSharedChatState hook
 */
export interface UseSharedChatStateReturn {
  state: SharedChatState;
  setters: SharedChatStateSetters;
  /** Reset all state to initial values */
  resetState: () => void;
}

/**
 * Hook to manage shared chat state that persists across popup/fullscreen mode switches
 * 
 * @example
 * ```tsx
 * // In ChatBot.tsx
 * const { state, setters, resetState } = useSharedChatState();
 * 
 * // Pass to ChatWindow/FullScreenChatbot
 * <ChatWindow sharedState={state} sharedStateSetters={setters} />
 * ```
 */
export const useSharedChatState = (): UseSharedChatStateReturn => {
  // Message state
  const [messages, setMessages] = useState<MessageInterface[]>([]);
  const [statusMessage, setStatusMessage] = useState<MessageInterface | null>(null);
  const [pendingMessage, setPendingMessage] = useState<PendingMessage>(DEFAULT_PENDING_MESSAGE);
  
  // Event polling state
  const [events, setEvents] = useState<ParlantEvent[]>([]);
  const [lastOffset, setLastOffset] = useState<number>(0);

  // Reset all state to initial values
  const resetState = useCallback(() => {
    setMessages([]);
    setStatusMessage(null);
    setPendingMessage(DEFAULT_PENDING_MESSAGE);
    setEvents([]);
    setLastOffset(0);
  }, []);

  // Memoize state object to prevent unnecessary re-renders
  const state = useMemo(() => ({
    messages,
    statusMessage,
    pendingMessage,
    events,
    lastOffset,
  }), [messages, statusMessage, pendingMessage, events, lastOffset]);

  // Memoize setters object for stable reference across renders
  // Individual setters are already stable from useState
  const setters = useMemo(() => ({
    setMessages,
    setStatusMessage,
    setPendingMessage,
    setEvents,
    setLastOffset,
  }), [setMessages, setStatusMessage, setPendingMessage, setEvents, setLastOffset]);

  return {
    state,
    setters,
    resetState,
  };
};
